"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Lexer = exports.LexerError = void 0;
const reader_1 = __importDefault(require("./reader"));
const token_1 = require("./token");
const constants_1 = require("./constants");
const strings_1 = require("./strings");
const MAXIMUM_INDENT_LENGTH = 100000; // bug catcher
const MAXIMUM_NUMBER_LENGTH = 100000;
const MAXIMUM_STRING_LENGTH = 100000;
class LexerError extends Error {
    constructor(message, cursor) {
        super(message);
        this.message = message;
        this.name = 'LexerError';
        this.stack = new Error().stack;
        this.cursor = cursor;
    }
}
exports.LexerError = LexerError;
/**
 * Converts FQL code into tokens.
 * @param code the FQL code to convert
 * @throws LexerError if something goes wrong
 */
function lex(code) {
    try {
        const lexer = new Lexer(code);
        return { tokens: lexer.lex() };
    }
    catch (error) {
        return { tokens: [], error };
    }
}
exports.default = lex;
class Lexer {
    constructor(code) {
        this.reader = new reader_1.default(code);
        this.cursor = {
            line: 0,
            column: 0
        };
    }
    lex() {
        const tokens = [];
        while (true) {
            const { char, isEOS } = this.next();
            if (isEOS) {
                tokens.push(token_1.t.EOS());
                return tokens;
            }
            if (strings_1.isWhitespace(char)) {
                continue;
            }
            if (char === '!') {
                const nextChar = this.peek();
                if (strings_1.isAlpha(nextChar) || nextChar === '(') {
                    tokens.push(token_1.t.Operator('!'));
                    continue;
                }
            }
            if (strings_1.isAlpha(char) || char === '!' || char === '=' || char === '>' || char === '<' || char === '\\' || char === '_') {
                tokens.push(this.lexOperatorOrConditional(char));
                continue;
            }
            if (strings_1.isNumber(char) || char === '-' || char === '+') {
                tokens.push(this.lexNumber(char));
                continue;
            }
            if (char === '"' || char === '\'') {
                tokens.push(this.lexString(char));
                continue;
            }
            if (char === '.') {
                tokens.push(token_1.t.Dot());
                continue;
            }
            if (char === '[') {
                tokens.push(token_1.t.BrackLeft());
                continue;
            }
            if (char === ']') {
                tokens.push(token_1.t.BrackRight());
                continue;
            }
            if (char === ',') {
                tokens.push(token_1.t.Comma());
                continue;
            }
            if (char === '(') {
                tokens.push(token_1.t.ParenLeft());
                continue;
            }
            if (char === ')') {
                tokens.push(token_1.t.ParenRight());
                continue;
            }
            throw new LexerError(`invalid character "${char}"`, this.cursor);
        }
    }
    lexString(openQuote) {
        let str = '';
        // Looking for closing string of same type of quote (single or double)
        while (this.peek() !== openQuote) {
            const { char, isEOS } = this.next();
            str += char;
            if (isEOS) {
                throw new LexerError('unterminated string', this.cursor);
            }
            if (str.length >= MAXIMUM_STRING_LENGTH) {
                throw new LexerError('unreasonable string length', this.cursor);
            }
        }
        this.accept(openQuote); // Eat the last quote
        return token_1.t.String(`${openQuote}${str}${openQuote}`);
    }
    lexNumber(previous) {
        let str = '';
        let comingUp = this.peek();
        let isDecimal = false;
        while (strings_1.isNumber(comingUp) || comingUp === '.') {
            const { char } = this.next();
            str += char;
            // Prevent multiple decimal points and stray decimal points
            if (comingUp === '.') {
                if (strings_1.isTerminator(this.peek())) {
                    throw new LexerError('unexpected terminator after decimal point', this.cursor);
                }
                if (isDecimal) {
                    throw new LexerError('multiple decimal points in one number', this.cursor);
                }
                isDecimal = true;
            }
            // Prevent infinite loops
            if (str.length >= MAXIMUM_NUMBER_LENGTH) {
                throw new LexerError('unreasonable number length', this.cursor);
            }
            comingUp = this.peek();
        }
        return token_1.t.Number(previous + str);
    }
    lexOperatorOrConditional(previous) {
        // =
        if (previous === '=') {
            return token_1.t.Operator('=');
        }
        // !=
        if (previous === '!') {
            if (this.accept('=')) {
                return token_1.t.Operator('!=');
            }
            throw new LexerError(`expected '=' after '!', got '${this.peek()}'`, this.cursor);
        }
        // and
        if (previous === 'a') {
            if (this.accept('nd')) {
                return token_1.t.Conditional('and');
            }
            return this.lexIdent(previous);
        }
        // or
        if (previous === 'o') {
            if (this.accept('r')) {
                return token_1.t.Conditional('or');
            }
            return this.lexIdent(previous);
        }
        // null
        if (previous === 'n') {
            if (this.accept('ull')) {
                return token_1.t.Null();
            }
            return this.lexIdent(previous);
        }
        // <=, >=, <, >
        if (previous === '<' || previous === '>') {
            if (this.accept('=')) {
                return token_1.t.Operator(previous + '=');
            }
            return token_1.t.Operator(previous);
        }
        // all other idents
        return this.lexIdent(previous);
    }
    lexIdent(previous) {
        /* this function differs from the go implementation in that the go impl
         hasn't yet advanced the character so will do a peek/next loop.  here we
         have already done a next() so need to check to see if the first char is
         an escape sequence */
        let str = '';
        let char = previous;
        while (true) {
            // Allow escaping of any character except EOS
            if (char === '\\') {
                if (this.peek() === constants_1.EOS_FLAG) {
                    throw new LexerError('expected character after escape character, got EOS', this.cursor);
                }
                char = this.next().char;
            }
            str += char;
            if (str.length >= MAXIMUM_INDENT_LENGTH) {
                throw new LexerError('unreasonable literal length', this.cursor);
            }
            if (!strings_1.isIdent(this.peek())) {
                break;
            }
            char = this.next().char;
        }
        const comingUp = this.peek();
        if (!(comingUp === constants_1.EOS_FLAG ||
            strings_1.isTerminator(comingUp) ||
            comingUp === '.' ||
            comingUp === '(' ||
            comingUp === '=' ||
            comingUp === '!')) {
            throw new LexerError(`expected termination character after identifier, got ${comingUp}`, this.cursor);
        }
        return token_1.t.Ident(str);
    }
    /**
     * Helpers
     */
    // Attempts to advance the string, rolls back and returns false if it can't.
    accept(str) {
        let chars = '';
        for (const _ of str) {
            const { char, isEOS } = this.next();
            chars += char;
            if (isEOS) {
                return false;
            }
            if (strings_1.isTerminator(char)) {
                break;
            }
        }
        if (str === chars && strings_1.isTerminator(this.peek())) {
            return true;
        }
        this.backup(chars.length);
        return false;
    }
    next() {
        const { char, isEOS } = this.reader.forward();
        if (strings_1.isNewLine(char)) {
            this.cursor.line += 1;
            this.cursor.column = 0;
        }
        else {
            this.cursor.column += 1;
        }
        const c = isEOS ? constants_1.EOS_FLAG : char;
        return { char: c, isEOS };
    }
    // Looks at the next character and then goes back
    peek() {
        const { char, isEOS } = this.next();
        if (!isEOS) {
            this.backup(1);
        }
        return char;
    }
    backup(count) {
        for (let n = count; n > 0; n--) {
            let char;
            try {
                char = this.reader.backward().char;
            }
            catch (err) {
                return;
            }
            if (strings_1.isNewLine(char)) {
                this.cursor.line -= 1;
                this.cursor.column = 0;
            }
            else {
                this.cursor.column -= 1;
            }
        }
    }
}
exports.Lexer = Lexer;
//# sourceMappingURL=lexer.js.map